import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { learningPortfolioActions } from '../actions/learning-portfolio.actions';
import { LearningPortfolioAttachment, LearningPortfolioNoteUpdate, LearningPortfolioNoteValue, LearningPortfolioParams } from '../models';
import {
    selectAttachments,
    selectAttachmentsFailed,
    selectAttachmentsUploading,
    selectCategories,
    selectCategoriesSaving,
    selectCreatedCategory,
    selectDeleted,
    selectPendingUploads,
    selectNote,
    selectNoteLoaded,
    selectNoteLoading,
    selectNotes,
    selectNotesLoaded,
    selectNotesLoading,
    selectNotesTotal,
    selectSaved,
    selectSaving,
} from '../selectors/learning-portfolio.selector';

@Injectable({ providedIn: 'root' })
export class LearningPortfolioFacade {
    private readonly store = inject(Store);

    readonly notes = this.store.selectSignal(selectNotes);
    readonly total = this.store.selectSignal(selectNotesTotal);
    readonly loaded = this.store.selectSignal(selectNotesLoaded);
    readonly loading = this.store.selectSignal(selectNotesLoading);
    readonly categories = this.store.selectSignal(selectCategories);
    readonly categoriesSaving = this.store.selectSignal(selectCategoriesSaving);
    readonly createdCategory = this.store.selectSignal(selectCreatedCategory);
    readonly saving = this.store.selectSignal(selectSaving);
    readonly deleted = this.store.selectSignal(selectDeleted);
    readonly attachments = this.store.selectSignal(selectAttachments);
    readonly attachmentsUploading = this.store.selectSignal(selectAttachmentsUploading);
    readonly pendingUploads = this.store.selectSignal(selectPendingUploads);
    readonly attachmentsFailed = this.store.selectSignal(selectAttachmentsFailed);

    readonly note = (id: string) => this.store.selectSignal(selectNote(id));
    readonly noteLoaded = (id: string) => this.store.selectSignal(selectNoteLoaded(id));
    readonly noteLoading = (id: string) => this.store.selectSignal(selectNoteLoading(id));

    readonly saved$ = this.store.select(selectSaved);
    readonly deleted$ = this.store.select(selectDeleted);
    readonly note$ = (id: string) => this.store.select(selectNote(id));

    getNotes(params: LearningPortfolioParams = {}): void {
        this.store.dispatch(learningPortfolioActions.getNotes({ params }));
    }

    getNote(id: string): void {
        this.store.dispatch(learningPortfolioActions.getNote({ id }));
    }

    getCategories(): void {
        this.store.dispatch(learningPortfolioActions.getCategories());
    }

    createCategory(name: string): void {
        this.store.dispatch(learningPortfolioActions.createCategory({ name }));
    }

    createNote(value: LearningPortfolioNoteValue): void {
        this.store.dispatch(learningPortfolioActions.createNote({ value }));
    }

    updateNote(id: string, value: LearningPortfolioNoteUpdate): void {
        this.store.dispatch(learningPortfolioActions.updateNote({ id, value }));
    }

    uploadAttachments(files: File[]): void {
        this.store.dispatch(learningPortfolioActions.uploadAttachments({ uploadId: crypto.randomUUID(), files }));
    }

    removeAttachment(id: string): void {
        this.store.dispatch(learningPortfolioActions.removeAttachment({ id }));
    }

    setAttachments(payload: LearningPortfolioAttachment[]): void {
        this.store.dispatch(learningPortfolioActions.setAttachments({ payload }));
    }

    clearAttachments(): void {
        this.store.dispatch(learningPortfolioActions.clearAttachments());
    }

    deleteNote(id: string): void {
        this.store.dispatch(learningPortfolioActions.deleteNote({ id }));
    }

    clear(): void {
        this.store.dispatch(learningPortfolioActions.clear());
    }
}
